"use client";

import { useEffect, useState, useCallback } from "react";
import Pagination from "../Pagination";
import CategoryArticlesSkeleton from "../skeleton/CategoryArticlesSkeleton";

type Video = {
  id: string;
  title: string;
  description: string;
  youtubeUrl: string;
};

const VideosPodcasts = () => {
  const [videos, setVideos] = useState<Video[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // ✅ Fetch videos from Strapi API
  const fetchVideos = useCallback(async () => {
    setLoading(true);
    setErrorMessage(null);

    try {
      const response = await fetch(`/api/videos?page=${page}`);
      const data = await response.json();
      console.log(data);

      if (!response.ok || data.error) {
        throw new Error("Impossible de charger les vidéos.");
      }

      // ✅ Convert YouTube URLs to embed format
      const formattedVideos = data.data.map((video: any) => {
        const rawUrl = video.url; // Assume Strapi stores the URL in `url`
        let embedUrl = rawUrl;

        if (rawUrl.includes("youtu.be")) {
          // Convert short YouTube URL: https://youtu.be/-iV8qY5hDSQ → https://www.youtube.com/embed/-iV8qY5hDSQ
          embedUrl = rawUrl.replace("youtu.be/", "www.youtube.com/embed/");
        } else if (rawUrl.includes("watch?v=")) {
          // Convert normal YouTube URL: https://www.youtube.com/watch?v=-iV8qY5hDSQ → https://www.youtube.com/embed/-iV8qY5hDSQ
          embedUrl = rawUrl.replace("watch?v=", "embed/");
        }

        return {
          id: String(video.id),
          title: video.title,
          description: video.description,
          youtubeUrl: embedUrl,
        };
      });

      setVideos(formattedVideos);
      setTotalPages(data.meta.pagination.pageCount || 1);

      // ✅ Smooth scroll after changing pages
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (error: any) {
      setVideos([]);
      setTotalPages(1);
      setErrorMessage(error.message);
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    fetchVideos();
  }, [fetchVideos]);

  if (loading) return <CategoryArticlesSkeleton />;

  return (
    <div className="container mx-auto p-2">
      <h1 className="mb-6 text-center text-3xl font-bold text-blue-600">
        Vidéos & Podcasts
      </h1>

      {/* ✅ Error Message */}
      {errorMessage && (
        <p className="text-center text-red-500">{errorMessage}</p>
      )}

      {/* ✅ Display YouTube Videos */}
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
        {videos.length > 0 ? (
          videos.map((video) => (
            <div key={video.id} className="rounded-lg border p-4 shadow-lg">
              <div className="aspect-w-16 aspect-h-9 relative">
                <iframe
                  width="100%"
                  height="200"
                  src={video.youtubeUrl.replace("watch?v=", "embed/")}
                  title={video.title}
                  allowFullScreen
                  className="rounded-lg"
                />
              </div>
              <h3 className="mt-3 text-lg font-semibold">{video.title}</h3>
              <p
                className="text-sm text-gray-700 dark:text-gray-300"
                aria-label={`Description of ${video.title}: ${video.description}`}
                lang="fr"
              >
                {video.description}
              </p>
            </div>
          ))
        ) : (
          <p className="col-span-full text-center text-gray-500">
            Aucune vidéo disponible.
          </p>
        )}
      </div>

      {/* ✅ Pagination */}
      {totalPages > 1 && (
        <div className="mt-8 flex flex-col items-center space-y-2">
          <span className="text-sm text-gray-700 dark:text-gray-300">
            Page {page} sur {totalPages}
          </span>
          <Pagination
            currentPage={page}
            totalPages={totalPages}
            onPageChange={(newPage) => setPage(newPage)}
            showIcons
          />
        </div>
      )}
    </div>
  );
};

export default VideosPodcasts;
